import { useEffect, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon.js";

/**
 * Something over the screen: a centred panel on a desktop and a sheet from the
 * bottom on a phone. Escape and a tap outside both close it.
 *
 * Without a `title` there is no header, for content that brings its own (the
 * stock panels): `label` then names the dialog for a screen reader. `wide` is
 * for content with a table in it.
 *
 * It is drawn into `document.body`, not where it is written, because a dialog
 * is often opened from inside a form -- the serial scanner sits in Receive's
 * and the item form's -- and a form inside a form is not a form a browser
 * keeps apart: pressing "Add, scan the next" fired neither form's handler and
 * tried a plain page submit instead, which the CSP's `form-action 'none'`
 * refused without a word. The button simply did nothing. A React event still
 * bubbles through a portal to the components around it, so a submit is also
 * stopped at the dialog, or it would reach the outer form's handler.
 */
export function Dialog({
  title,
  label,
  wide = false,
  onClose,
  children,
}: {
  title?: string;
  label?: string;
  wide?: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <>
      <div className="dialog-scrim" aria-hidden="true" onClick={onClose} />
      <div
        className={wide ? "dialog dialog-wide" : "dialog"}
        role="dialog"
        aria-modal="true"
        {...(title ? { "aria-labelledby": titleId } : { "aria-label": label })}
        onSubmit={(event) => event.stopPropagation()}
      >
        {title && (
          <header className="dialog-head">
            <h2 id={titleId} className="dialog-title">
              {title}
            </h2>
            <button type="button" className="icon-button" aria-label="Close" onClick={onClose}>
              <Icon name="x" size={18} />
            </button>
          </header>
        )}
        {children}
      </div>
    </>,
    document.body,
  );
}
